import { useState } from 'react';
import type { EmailMessage } from '@epasu/core';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MessageBody } from '@/components/MessageBody';
import { ReplyDialog } from '@/components/ReplyDialog';

interface Props {
  mails: EmailMessage[];
  onChanged: () => void;
}

/**
 * Schriftverkehr über alle Ansprechpartner der Firma hinweg.
 *
 * Das „E-Mail“-Anlegen bleibt im Kopf der Firmenakte — es adressiert alle
 * Ansprechpartner, nicht eine einzelne Nachricht dieser Liste. Antworten auf
 * eine bestimmte Nachricht gehört dagegen hierher.
 */
export function MailTab({ mails, onChanged }: Props) {
  const [replying, setReplying] = useState<EmailMessage | null>(null);
  const [offeneMail, setOffeneMail] = useState<string | null>(null);

  return (
    <>
      <Card className="p-6">
        <h2 className="text-sm font-semibold">
          Schriftverkehr
          <span className="ml-2 font-normal text-muted-foreground">
            über alle Ansprechpartner
          </span>
        </h2>

        {mails.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Noch keine Nachrichten. Sie erscheinen hier automatisch, sobald das
            Postfach abgerufen wurde.
          </p>
        ) : (
          <ul className="mt-3 divide-y">
            {mails.map((m) => (
              <li key={m.id}>
                <button
                  className="w-full py-3 text-left hover:bg-muted/30"
                  onClick={() => setOffeneMail(offeneMail === m.id ? null : m.id)}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className={`truncate text-sm ${m.seen ? '' : 'font-semibold'}`}>
                      {m.direction === 'outbound' && (
                        <span className="mr-1 text-muted-foreground">↗</span>
                      )}
                      {m.subject || '(ohne Betreff)'}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {m.receivedAt
                        ? new Date(m.receivedAt).toLocaleDateString('de-DE')
                        : ''}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {m.direction === 'outbound' ? `An ${m.toAddress}` : m.fromAddress}
                    {m.snippet && ` — ${m.snippet}`}
                  </p>
                </button>

                {offeneMail === m.id && (
                  <div className="pb-4">
                    <MessageBody text={m.body} />
                    <div className="mt-2">
                      <Button variant="outline" size="sm" onClick={() => setReplying(m)}>
                        Antworten
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {replying && (
        <ReplyDialog
          message={replying}
          onClose={() => setReplying(null)}
          onSent={() => {
            setReplying(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}
